import type { LegalCommon, LegalDocument } from "@/components/LegalPageLayout";

const legalEyebrow = "Rechtliches";
const updated = "Zuletzt aktualisiert: 1. August 2026";

/**
 * Übersetzung von legal-sl.ts. Maßgeblich ist die slowenische Fassung (siehe
 * Schlussbestimmung der AGB); diese Datei bei Änderungen dort nachziehen.
 */
export const legalDe = {
  common: {
    tocHeading: "Inhalt",
    company: {
      legalName: "Firma",
      address: "Sitz",
      registrationNumber: "Registernummer",
      vatNumber: "USt-IdNr.",
      taxNumber: "Steuernummer",
      registryEntry: "Registereintrag",
      shareCapital: "Stammkapital",
      representative: "Vertreten durch",
      email: "E-Mail",
      phone: "Telefon",
      website: "Website",
    },
  } satisfies LegalCommon,

  terms: {
    eyebrow: legalEyebrow,
    title: "Allgemeine Geschäfts- und Nutzungsbedingungen",
    updated,
    intro:
      "Diese Bedingungen regeln die Nutzung der Website nreport.ai und die Bestellung von Immobilienberichten. Website und Dienst werden von der Noventis d.o.o. betrieben. Bitte lesen Sie die Bedingungen aufmerksam — mit der Nutzung des Dienstes bestätigen Sie, dass Sie sie gelesen und akzeptiert haben.",
    sections: [
      {
        title: "1. Anbieterangaben",
        blocks: [
          {
            type: "p",
            text: "Die Website nreport.ai und der Dienst nReport werden betrieben und erbracht von:",
          },
          { type: "company" },
          {
            type: "p",
            text: "Die Angaben werden gemäß Artikel 6 des slowenischen Gesetzes über den elektronischen Geschäftsverkehr (ZEPT) und Artikel 45 des Gesellschaftsgesetzes (ZGD-1) veröffentlicht. Im Folgenden wird die Noventis d.o.o. als „Anbieter“, „wir“ oder „nReport“ bezeichnet, die den Dienst nutzende Person als „Nutzer“ oder „Sie“.",
          },
        ],
      },
      {
        title: "2. Geltungsbereich und Annahme",
        blocks: [
          {
            type: "p",
            text: "Diese Bedingungen gelten für alle Besucher der Website, registrierte Nutzer und Käufer von Berichten, sowohl Verbraucher als auch Unternehmer. Sie sind Bestandteil jedes über die Website geschlossenen Vertrags.",
          },
          {
            type: "p",
            text: "Sie akzeptieren die Bedingungen mit der Bestellung. Vor Abschluss des Kaufs stehen Ihnen die Bedingungen stets in einer speicher- und druckbaren Form zur Verfügung. Wenn Sie sie nicht akzeptieren, dürfen Sie den Dienst nicht nutzen.",
          },
          {
            type: "p",
            text: "Verbraucher ist jede natürliche Person, die den Dienst zu Zwecken erwirbt oder nutzt, die überwiegend weder ihrer gewerblichen noch ihrer beruflichen Tätigkeit zugerechnet werden können. Ausdrücklich als Verbraucherbestimmungen gekennzeichnete Regelungen gelten nur für Verbraucher; die zwingenden Bestimmungen des slowenischen Verbraucherschutzgesetzes (ZVPot-1) gelten unabhängig von diesen Bedingungen und werden durch sie nicht eingeschränkt.",
          },
        ],
      },
      {
        title: "3. Leistungsbeschreibung",
        blocks: [
          {
            type: "p",
            text: "nReport ist ein Online-Dienst, der für eine einzelne Immobilie in der Republik Slowenien automatisch Daten aus öffentlich zugänglichen amtlichen Registern und aus öffentlich veröffentlichten Inseraten zusammenführt, verarbeitet und daraus einen strukturierten Bericht als PDF-Dokument erstellt.",
          },
          { type: "p", text: "Ein Bericht enthält in der Regel:" },
          {
            type: "ul",
            items: [
              "Daten aus dem Immobilienkataster und dem Immobilienregister (GURS),",
              "Daten zu abgeschlossenen Transaktionen aus dem Immobilienmarktregister (ETN),",
              "Angaben zum Grundbuchstand, soweit ein Auszug verfügbar ist,",
              "Daten zum Energieausweis, soweit vorhanden,",
              "eine Übersicht vergleichbarer öffentlich veröffentlichter Inserate,",
              "eine Beschreibung der Lage und der Umgebung,",
              "eine indikative Marktwerteinschätzung und eine mithilfe künstlicher Intelligenz erstellte textliche Analyse.",
            ],
          },
          {
            type: "p",
            text: "Der genaue Inhalt hängt von der Datenverfügbarkeit für die konkrete Immobilie ab. Ist eine Quelle nicht verfügbar oder enthält sie keinen Eintrag zur Immobilie, kann dieser Teil des Berichts verkürzt werden oder entfallen; darin liegt keine Schlechtleistung.",
          },
        ],
      },
      {
        title: "4. Charakter des Berichts — was er nicht ist",
        blocks: [
          {
            type: "note",
            text: "Ein nReport-Bericht ist ein Informationshilfsmittel. Er ist kein amtliches Wertgutachten und keine nach dem slowenischen Revisionsgesetz (ZRev-2) und den International Valuation Standards erstellte Bewertung. Er wird nicht von einem zertifizierten Immobiliensachverständigen erstellt und kann dessen Gutachten nicht ersetzen.",
          },
          { type: "p", text: "Der Bericht ist außerdem nicht:" },
          {
            type: "ul",
            items: [
              "eine Rechts-, Steuer-, Buchhaltungs- oder Anlageberatung,",
              "ein Ersatz für einen amtlichen Grundbuchauszug oder einen beglaubigten Katasterauszug,",
              "ein Ersatz für eine Besichtigung vor Ort, eine bautechnische Prüfung oder eine rechtliche Due Diligence,",
              "eine Entscheidungsgrundlage für Kreditgeber, Versicherer oder Behörden,",
              "ein Beweismittel in Gerichts-, Steuer- oder Verwaltungsverfahren.",
            ],
          },
          {
            type: "p",
            text: "Vor Abschluss eines Immobiliengeschäfts empfehlen wir, amtliche Grundbuch- und Katasterauszüge einzuholen und einen Rechtsanwalt, Notar, Immobilienmakler oder zertifizierten Sachverständigen zu konsultieren.",
          },
        ],
      },
      {
        title: "5. Einsatz künstlicher Intelligenz",
        blocks: [
          {
            type: "p",
            text: "Der Textteil des Berichts und die Wertschätzung entstehen automatisch mithilfe von Systemen künstlicher Intelligenz (große Sprachmodelle) und statistischen Modellen. Die Inhalte werden vor der Übergabe in der Regel nicht von einem Menschen geprüft.",
          },
          {
            type: "p",
            text: "Trotz sorgfältiger Auslegung können KI-Systeme unzutreffende, unvollständige oder irreführende Aussagen erzeugen, selbst wenn die Eingangsdaten korrekt sind. Prüfen Sie jede Angabe im Bericht, auf die Sie sich stützen wollen, anhand der amtlichen Originalquelle.",
          },
          {
            type: "p",
            text: "Wir verwenden weder die von Ihnen im Dienst eingegebenen Daten noch den Inhalt der erstellten Berichte zum Training von KI-Modellen und untersagen dies den von uns eingesetzten Modellanbietern vertraglich.",
          },
        ],
      },
      {
        title: "6. Datenquellen und deren Aktualität",
        blocks: [
          {
            type: "p",
            text: "Berichte beruhen auf öffentlich zugänglichen Quellen, unter anderem den Registern der Vermessungs- und Kartierungsbehörde der Republik Slowenien (Immobilienkataster, Immobilienregister, Immobilienmarktregister), dem Grundbuch, dem Register der Energieausweise, kartografischen Quellen sowie öffentlich veröffentlichten Immobilieninseraten.",
          },
          {
            type: "p",
            text: "Der Anbieter ist nicht Urheber dieser Daten und hat keinen Einfluss auf deren Richtigkeit, Vollständigkeit oder Aktualität. Amtliche Register werden zeitversetzt aktualisiert; ihr Inhalt kann vom tatsächlichen Zustand der Immobilie abweichen (etwa wegen nicht gemeldeter Baumaßnahmen, nicht abgeglichener Eintragungen oder Fehlern im Quellregister). Aus Inseraten übernommene Daten geben den vom Inserenten geforderten Preis wieder, nicht den erzielten Preis.",
          },
          {
            type: "p",
            text: "Alle Daten in einem Bericht beziehen sich auf den Zeitpunkt der Erstellung. Berichte werden danach nicht automatisch aktualisiert.",
          },
        ],
      },
      {
        title: "7. Nutzerkonto",
        blocks: [
          {
            type: "ul",
            items: [
              "Für den Zugriff auf bestellte Berichte ist ein Nutzerkonto mit gültiger E-Mail-Adresse erforderlich. Sie können ein Konto mit Passwort oder über die Anmeldung mit Google oder Facebook erstellen.",
              "Kaufen Sie ohne vorherige Registrierung, erstellen wir automatisch ein Konto für die bei der Zahlung angegebene E-Mail-Adresse und senden Ihnen einen Anmeldelink.",
              "Die von Ihnen angegebenen Daten müssen wahrheitsgemäß und aktuell sein.",
              "Für den Schutz Ihres Passworts und für jede Nutzung des Dienstes über Ihr Konto sind Sie selbst verantwortlich. Melden Sie uns einen Verdacht auf unbefugte Nutzung unverzüglich.",
              "Ein Konto ist für eine Person bzw. ein Unternehmen bestimmt und darf weder übertragen noch geteilt werden.",
              "Der Dienst richtet sich nicht an Personen unter 18 Jahren.",
              "Sie können Ihr Konto jederzeit löschen; siehe den Abschnitt zur Datenlöschung in der Datenschutzerklärung.",
            ],
          },
        ],
      },
      {
        title: "8. Guthaben, Preise und Zahlung",
        blocks: [
          {
            type: "ul",
            items: [
              "Berichte werden mit Guthabenpunkten (Credits) bezahlt. Ein Credit erstellt und schaltet einen vollständigen Bericht für eine Immobilie frei.",
              "Die Preise sind auf der Website veröffentlicht und in Euro (EUR) angegeben. Der angezeigte Preis ist der Endpreis, den Sie zahlen. Der Anbieter ist nicht umsatzsteuerpflichtig; es wird daher keine Umsatzsteuer erhoben (Artikel 94 Abs. 1 des slowenischen UStG, ZDDV-1).",
              "Versandkosten fallen nicht an, da der Bericht elektronisch geliefert wird.",
              "Die Zahlung erfolgt im Voraus über den Zahlungsdienstleister Stripe. Der Anbieter erhält und speichert keine Zahlungskartendaten.",
              "Rechnungen werden elektronisch ausgestellt und an Ihre E-Mail-Adresse gesendet. Mit der Bestellung stimmen Sie der elektronischen Rechnungsstellung zu.",
              "Gekaufte Credits sind ab dem Kaufdatum 1 Jahr gültig, wie in der Preisübersicht angegeben. Nicht verbrauchte Credits werden nach Ablauf der Gültigkeit nicht ersetzt, sofern nichts anderes vereinbart wird.",
              "Credits werden nicht in bar ausgezahlt, außer wenn Gesetz oder diese Bedingungen es vorsehen (etwa beim Widerruf).",
            ],
          },
        ],
      },
      {
        title: "9. Vertragsschluss und Leistungserbringung",
        blocks: [
          {
            type: "p",
            text: "Die Darstellung des Dienstes auf der Website ist eine Aufforderung zur Abgabe eines Angebots. Der Vertrag kommt zustande, wenn Sie mit einer Schaltfläche bestellen, die ausdrücklich auf die Zahlungspflicht hinweist, und die Zahlung erfolgreich bestätigt wird. Die Bestellbestätigung senden wir Ihnen zusammen mit diesen Bedingungen auf einem dauerhaften Datenträger per E-Mail.",
          },
          {
            type: "p",
            text: "Die Erstellung des Berichts beginnt unmittelbar nach Bestätigung der Zahlung bzw. unmittelbar nachdem Sie einen Credit für die gewählte Immobilie einsetzen. Ein Bericht liegt in der Regel innerhalb weniger Minuten vor; müssen Daten aus langsamen oder vorübergehend nicht erreichbaren externen Registern abgerufen werden, kann es länger dauern, längstens jedoch 48 Stunden. Der Bericht ist in Ihrem Nutzerkonto abrufbar; eine Kopie erhalten Sie zusätzlich per E-Mail.",
          },
          {
            type: "p",
            text: "Kann ein Bericht aus Gründen in unserer Sphäre nicht erstellt werden, schreiben wir den Credit Ihrem Konto wieder gut oder erstatten auf Ihren Wunsch den gezahlten Preis.",
          },
        ],
      },
      {
        title: "10. Widerrufsrecht für Verbraucher",
        blocks: [
          {
            type: "p",
            text: "Nach Artikel 134 ZVPot-1 haben Verbraucher das Recht, binnen 14 Tagen ab Vertragsschluss ohne Angabe von Gründen von einem Fernabsatzvertrag zurückzutreten. Eine eindeutige Erklärung an info@nreport.ai genügt; Sie können auch das amtliche slowenische Widerrufsformular verwenden.",
          },
          {
            type: "p",
            text: "Für den Kauf von Credits bedeutet dies: nicht verbrauchte Credits erstatten wir binnen 14 Tagen ohne Begründung vollständig. Die Erstattung erfolgt über dasselbe Zahlungsmittel, spätestens 14 Tage nach Eingang der Widerrufserklärung.",
          },
          {
            type: "note",
            text: "Ausnahme für bereits erstellte Berichte: Nach Artikel 135 Abs. 1 Nr. 13 ZVPot-1 besteht kein Widerrufsrecht bei digitalen Inhalten, die nicht auf einem körperlichen Datenträger geliefert werden, wenn die Ausführung mit ausdrücklicher vorheriger Zustimmung des Verbrauchers und seiner Kenntnisnahme begonnen hat, dass er dadurch sein Widerrufsrecht verliert. Wir holen diese Zustimmung deshalb vor der Zahlung ausdrücklich ein. Sobald der Bericht für die gewählte Immobilie erstellt und Ihnen bereitgestellt wurde, besteht für den verbrauchten Credit kein Widerrufsrecht mehr.",
          },
          {
            type: "p",
            text: "Erteilen Sie diese Zustimmung nicht, ist ein Kauf über die Website nicht möglich; kontaktieren Sie uns in diesem Fall unter info@nreport.ai, damit wir eine andere Abwicklung vereinbaren.",
          },
          {
            type: "p",
            text: "Das Widerrufsrecht gilt nicht für juristische Personen, Einzelunternehmer und andere Personen, die den Dienst im Rahmen ihrer Tätigkeit bestellen.",
          },
        ],
      },
      {
        title: "11. Vertragsmäßigkeit digitaler Inhalte und Gewährleistung",
        blocks: [
          {
            type: "p",
            text: "Wir verpflichten uns, einen Bericht zu liefern, der im Sinne der ZVPot-1-Bestimmungen über digitale Inhalte und digitale Dienstleistungen vertragsgemäß ist: Er entspricht der Beschreibung in diesen Bedingungen und auf der Website, eignet sich für den gewöhnlichen Verwendungszweck solcher Inhalte und wird im vereinbarten Umfang geliefert.",
          },
          {
            type: "p",
            text: "Ist ein Bericht nicht vertragsgemäß, kann der Verbraucher unter den Voraussetzungen des ZVPot-1 die Herstellung des vertragsgemäßen Zustands (Neuerstellung oder Ergänzung des Berichts), eine Preisminderung oder den Vertragsrücktritt verlangen. Melden Sie die Vertragswidrigkeit mit einer Mangelbeschreibung an info@nreport.ai. Wir antworten spätestens innerhalb von 8 Tagen.",
          },
          {
            type: "p",
            text: "Eine Abweichung amtlicher Registerdaten vom tatsächlichen Zustand der Immobilie ist keine Vertragswidrigkeit des Berichts, da der Anbieter diese Daten weder erzeugt noch für sie einsteht — siehe Abschnitte 6 und 12.",
          },
        ],
      },
      {
        title: "12. Haftungsbeschränkung",
        blocks: [
          {
            type: "p",
            text: "Der Dienst ist als Informationshilfsmittel konzipiert. Der Anbieter übernimmt keine Gewähr für Richtigkeit, Vollständigkeit oder Aktualität von Daten aus externen Quellen und ebenso wenig für die Richtigkeit von Wertschätzungen oder mithilfe künstlicher Intelligenz erzeugten Textanalysen.",
          },
          {
            type: "p",
            text: "Mit der Nutzung des Dienstes stimmen Sie zu, eine Entscheidung über Kauf, Verkauf, Vermietung, Finanzierung oder Bewertung einer Immobilie nicht allein auf einen Bericht zu stützen. Der Anbieter haftet nicht für auf Grundlage eines Berichts getroffene geschäftliche Entscheidungen und ebenso wenig für entgangenen Gewinn, entgangene Gelegenheiten oder sonstige mittelbare Schäden und Folgeschäden.",
          },
          {
            type: "p",
            text: "Die Gesamthaftung des Anbieters aus einer einzelnen Bestellung ist auf den Betrag begrenzt, den der Nutzer für diese Bestellung gezahlt hat, soweit zwingendes Recht nichts anderes bestimmt.",
          },
          {
            type: "note",
            text: "Keine Bestimmung dieser Bedingungen schließt die Haftung des Anbieters für Vorsatz oder grobe Fahrlässigkeit, für Schäden an Leben oder Gesundheit sowie die gesetzlichen Verbraucherrechte nach ZVPot-1 und dem slowenischen Obligationengesetzbuch aus oder beschränkt sie.",
          },
          {
            type: "p",
            text: "Der Dienst wird „wie besehen“ bereitgestellt. Wir bemühen uns um einen unterbrechungsfreien Betrieb, gewährleisten jedoch nicht, dass der Dienst stets fehler- und unterbrechungsfrei verfügbar ist; geplante Wartungen und Ausfälle externer Datenquellen sind zu erwarten.",
          },
        ],
      },
      {
        title: "13. Zulässige Nutzung und Verbote",
        blocks: [
          { type: "p", text: "Bei der Nutzung des Dienstes ist es untersagt:" },
          {
            type: "ul",
            items: [
              "Inhalte der Website systematisch, automatisiert oder massenhaft auszulesen (etwa durch Scraping, Bots oder Data-Mining),",
              "Zugang zu Systemteilen zu erlangen, zu denen Sie nicht berechtigt sind, oder Sicherheitsmechanismen zu umgehen,",
              "die Infrastruktur zu überlasten oder den Betrieb des Dienstes zu stören,",
              "den Dienst zur Belästigung, Nachstellung oder Profilbildung über Einzelpersonen zu nutzen,",
              "Berichte für Immobilien zu bestellen, an denen Sie kein berechtigtes Interesse haben,",
              "Berichte oder Teile davon ohne unsere vorherige schriftliche Erlaubnis weiterzuverkaufen, zu verbreiten, zu veröffentlichen oder in eigene Produkte einzubinden,",
              "Urheber- und Quellenangaben sowie Hinweise im Bericht zu entfernen oder unkenntlich zu machen.",
            ],
          },
          {
            type: "p",
            text: "Bei einem Verstoß können wir den Zugang beschränken oder aussetzen, bei schweren oder wiederholten Verstößen das Konto schließen. Wir informieren Sie über die Maßnahme und geben Ihnen, soweit möglich, Gelegenheit zur Abhilfe.",
          },
        ],
      },
      {
        title: "14. Geistiges Eigentum und Nutzungslizenz",
        blocks: [
          {
            type: "p",
            text: "Die Website, ihr Design, der Quellcode, Texte, Grafiken, die Marke nReport sowie Struktur, Auswahl und Darstellung der Daten in Berichten sind urheberrechtlich und durch das Datenbankherstellerrecht (slowenisches Urheberrechtsgesetz) geschützt. Alle Rechte vorbehalten.",
          },
          {
            type: "p",
            text: "Mit dem Kauf eines Berichts erwerben Sie ein nicht ausschließliches, nicht übertragbares und zeitlich unbefristetes Recht, den Bericht für eigene Zwecke zu nutzen, einschließlich der Weitergabe an Ihre Berater, Ihre Bank, einen Immobilienmakler oder die Gegenseite eines konkreten Geschäfts. Jede darüber hinausgehende Nutzung, insbesondere Veröffentlichung, Weiterverkauf oder systematische Vervielfältigung, bedarf unserer vorherigen schriftlichen Erlaubnis.",
          },
          {
            type: "p",
            text: "Daten aus amtlichen öffentlichen Registern stehen den zuständigen Stellen zu bzw. werden von ihnen verwaltet und werden im Bericht als Information über den öffentlich zugänglichen Stand verwendet; Rechte dieser Stellen gehen dadurch nicht auf den Nutzer über.",
          },
        ],
      },
      {
        title: "15. Teilen eines Berichts per Link",
        blocks: [
          {
            type: "p",
            text: "Der Dienst ermöglicht es, für Ihren Bericht einen Freigabelink zu erstellen. Jede Person, die den Link kennt, kann den Bericht ohne Anmeldung einsehen — teilen Sie ihn daher überlegt. Sie können den Link jederzeit in Ihrem Konto widerrufen; danach ist der Bericht darüber nicht mehr erreichbar.",
          },
          {
            type: "p",
            text: "Für die geteilten Inhalte und die Folgen des Teilens sind Sie selbst verantwortlich.",
          },
        ],
      },
      {
        title: "16. Schutz personenbezogener Daten",
        blocks: [
          {
            type: "p",
            text: "Wir verarbeiten personenbezogene Daten nach der Datenschutz-Grundverordnung (DSGVO) und dem slowenischen Datenschutzgesetz (ZVOP-2). Einzelheiten zu Datenkategorien, Zwecken, Rechtsgrundlagen, Speicherfristen, Auftragsverarbeitern und Ihren Rechten finden Sie in der Datenschutzerklärung, die Bestandteil dieser Bedingungen ist.",
          },
        ],
      },
      {
        title: "17. Beschwerden und außergerichtliche Streitbeilegung",
        blocks: [
          {
            type: "p",
            text: "Beschwerden können Sie an info@nreport.ai senden. Den Eingang bestätigen wir innerhalb von 5 Werktagen; über das Ergebnis informieren wir Sie spätestens innerhalb von 8 Tagen. Das Beschwerdeverfahren ist vertraulich.",
          },
          {
            type: "p",
            text: "Gemäß Artikel 32 des slowenischen Gesetzes über die außergerichtliche Beilegung von Verbraucherstreitigkeiten (ZIsRPS) teilen wir mit, dass der Anbieter keine Stelle zur außergerichtlichen Streitbeilegung als zuständig anerkennt. Das Recht des Verbrauchers, den Rechtsweg zu beschreiten oder sich an die zuständigen Behörden zu wenden, bleibt unberührt.",
          },
          {
            type: "p",
            text: "Die europäische Plattform zur Online-Streitbeilegung hat ihren Betrieb zum 20. Juli 2025 auf Grundlage der Verordnung (EU) 2024/3228 eingestellt; wir veröffentlichen deshalb keinen Link mehr darauf.",
          },
          {
            type: "p",
            text: "Verbraucher können sich außerdem an die Marktaufsichtsbehörde der Republik Slowenien, Dunajska cesta 160, 1000 Ljubljana, oder bei grenzüberschreitenden Streitigkeiten innerhalb der EU an das Europäische Verbraucherzentrum Slowenien wenden.",
          },
        ],
      },
      {
        title: "18. Änderungen der Bedingungen und des Dienstes",
        blocks: [
          {
            type: "p",
            text: "Wir können diese Bedingungen wegen Gesetzesänderungen, Änderungen des Dienstes oder zur Beseitigung von Unklarheiten anpassen. Die geltende Fassung wird stets auf dieser Seite mit dem Datum der letzten Aktualisierung veröffentlicht.",
          },
          {
            type: "p",
            text: "Über wesentliche Änderungen informieren wir registrierte Nutzer mindestens 15 Tage vor Inkrafttreten per E-Mail. Sind Sie mit einer Änderung nicht einverstanden, können Sie die Nutzung vor deren Inkrafttreten beenden und die Löschung Ihres Kontos verlangen; den für nicht verbrauchte Credits gezahlten Preis erstatten wir in diesem Fall.",
          },
          {
            type: "p",
            text: "Für bereits erteilte Bestellungen gelten die zum Bestellzeitpunkt geltenden Bedingungen.",
          },
        ],
      },
      {
        title: "19. Anwendbares Recht und Gerichtsstand",
        blocks: [
          {
            type: "p",
            text: "Für Vertragsverhältnisse nach diesen Bedingungen gilt das Recht der Republik Slowenien unter Ausschluss seiner Kollisionsnormen. Das UN-Kaufrecht findet keine Anwendung.",
          },
          {
            type: "p",
            text: "Für Streitigkeiten ist das sachlich zuständige Gericht in der Republik Slowenien zuständig. Bei Verbrauchern entzieht diese Wahl nicht den Schutz, den zwingende Vorschriften des Staates ihres gewöhnlichen Aufenthalts gewähren; Verbraucher können auch bei den Gerichten ihres Wohnsitzstaates klagen.",
          },
        ],
      },
      {
        title: "20. Schlussbestimmungen",
        blocks: [
          {
            type: "p",
            text: "Ist eine Bestimmung dieser Bedingungen unwirksam oder undurchführbar, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt; die unwirksame Bestimmung wird durch eine wirksame ersetzt, die ihrem wirtschaftlichen Zweck am nächsten kommt.",
          },
          {
            type: "p",
            text: "Diese Bedingungen werden in slowenischer, englischer, deutscher und russischer Sprache veröffentlicht. Bei Abweichungen zwischen den Sprachfassungen ist die slowenische Fassung maßgeblich.",
          },
          {
            type: "p",
            text: "Bei Fragen zu diesen Bedingungen erreichen Sie uns unter info@nreport.ai.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  privacy: {
    eyebrow: legalEyebrow,
    title: "Datenschutzerklärung",
    updated,
    intro:
      "Diese Erklärung beschreibt, welche personenbezogenen Daten die Noventis d.o.o. beim Betrieb der Website nreport.ai und der Erbringung des Dienstes nReport verarbeitet, warum und auf welcher Rechtsgrundlage, an wen sie weitergegeben werden, wie lange sie gespeichert werden und welche Rechte Ihnen zustehen. Sie ist nach der Verordnung (EU) 2016/679 (DSGVO) und dem slowenischen Datenschutzgesetz (ZVOP-2) erstellt.",
    sections: [
      {
        title: "1. Verantwortlicher und Kontakt",
        blocks: [
          { type: "p", text: "Verantwortlicher für die Datenverarbeitung ist:" },
          { type: "company" },
          {
            type: "p",
            text: "Bei allen Datenschutzfragen und zur Ausübung Ihrer Rechte schreiben Sie an privacy@nreport.ai oder an die Anschrift des Firmensitzes.",
          },
          {
            type: "p",
            text: "Angesichts von Art, Umfang und Zwecken der Verarbeitung ist der Verantwortliche nicht verpflichtet, einen Datenschutzbeauftragten zu benennen (Artikel 37 DSGVO). Sollte sich dies ändern, veröffentlichen wir die Kontaktdaten hier.",
          },
        ],
      },
      {
        title: "2. Welche Daten wir verarbeiten",
        blocks: [
          {
            type: "table",
            head: ["Kategorie", "Konkrete Daten", "Herkunft"],
            rows: [
              [
                "Kontodaten",
                "E-Mail-Adresse, Name (falls angegeben), Passwort als Hash, Kennung des Anmeldeanbieters (Google, Facebook), Registrierungsdatum, Sprache",
                "von Ihnen bzw. vom Anmeldeanbieter",
              ],
              [
                "Bestelldaten",
                "Adresse und Identifikation der abgefragten Immobilie, Wohnungsnummer, Immobilienart, erstellte Berichte, Guthabenstand, Nutzungsverlauf, Zeitpunkt des ersten Öffnens eines Berichts, des PDF-Downloads und des Aufrufs des Verhandlungsblatts",
                "von Ihnen und aus dem Betrieb des Dienstes",
              ],
              [
                "Zahlungsdaten",
                "Betrag, Währung, Zeitpunkt, Zahlungsstatus, Sitzungskennung beim Zahlungsdienstleister, Rechnungsangaben. Zahlungskartendaten erhalten und speichern wir nicht.",
                "vom Zahlungsdienstleister Stripe",
              ],
              [
                "Technische Daten",
                "IP-Adresse, Browser- und Geräteinformationen, Zugriffszeit, aufgerufene URLs, Fehlerprotokolle",
                "automatisch bei der Nutzung",
              ],
              [
                "Einwilligungsnachweise",
                "Ihre Cookie-Auswahl und deren Zeitpunkt, Newsletter-Einwilligung, Zustimmung zur sofortigen Ausführung beim Kauf",
                "von Ihnen",
              ],
              [
                "Kommunikation",
                "Inhalt Ihrer Nachrichten an uns und unsere Antworten",
                "von Ihnen",
              ],
              [
                "Nutzungs- und Marketingdaten",
                "Ereignisse in der Anwendung, Zugriffsquellen, Conversions — nur im Umfang Ihrer Cookie-Einwilligung",
                "automatisch, bei Einwilligung",
              ],
            ],
          },
          {
            type: "p",
            text: "Besondere Kategorien personenbezogener Daten nach Artikel 9 DSGVO (etwa Gesundheits-, Religions- oder politische Daten) erheben wir nicht; bitte übermitteln Sie uns solche Daten nicht.",
          },
        ],
      },
      {
        title: "3. Zwecke und Rechtsgrundlagen",
        blocks: [
          {
            type: "table",
            head: ["Zweck", "Rechtsgrundlage", "Speicherdauer"],
            rows: [
              [
                "Führung des Nutzerkontos und Anmeldung",
                "Vertragserfüllung — Art. 6 Abs. 1 lit. b DSGVO",
                "bis zur Kontolöschung; danach bis zu 30 Tage in Backups",
              ],
              [
                "Erstellung und Lieferung des bestellten Berichts",
                "Vertragserfüllung — Art. 6 Abs. 1 lit. b DSGVO",
                "für die Dauer des Kontos, damit Berichte abrufbar bleiben",
              ],
              [
                "Zahlungsabwicklung und Betrugsprävention",
                "Vertragserfüllung — Art. 6 Abs. 1 lit. b; rechtliche Verpflichtung — lit. c; berechtigtes Interesse — lit. f",
                "nach den Aufbewahrungsregeln für Buchhaltungsunterlagen",
              ],
              [
                "Ausstellung und Aufbewahrung von Rechnungen, Steuerpflichten",
                "rechtliche Verpflichtung — Art. 6 Abs. 1 lit. c DSGVO (ZDDV-1, ZGD-1)",
                "10 Jahre ab Ende des Jahres, auf das sich die Rechnung bezieht",
              ],
              [
                "Transaktionsbenachrichtigungen (Bestellbestätigung, Bericht fertig, Passwort-Reset)",
                "Vertragserfüllung — Art. 6 Abs. 1 lit. b DSGVO",
                "bis zur Kontolöschung",
              ],
              [
                "Nutzersupport und Beschwerdebearbeitung",
                "Vertragserfüllung — Art. 6 Abs. 1 lit. b; berechtigtes Interesse — lit. f",
                "3 Jahre nach Abschluss des Vorgangs",
              ],
              [
                "Systemsicherheit, Protokolle, Missbrauchsprävention",
                "berechtigtes Interesse — Art. 6 Abs. 1 lit. f DSGVO",
                "in der Regel 90 Tage; länger nur bei Untersuchung eines konkreten Vorfalls",
              ],
              [
                "Reichweitenmessung und Verbesserung des Dienstes (Analyse)",
                "Einwilligung — Art. 6 Abs. 1 lit. a DSGVO und Art. 225 ZEKom-2",
                "bis zum Widerruf; Cookies gemäß Cookie-Richtlinie",
              ],
              [
                "Werbung und Messung der Werbewirkung",
                "Einwilligung — Art. 6 Abs. 1 lit. a DSGVO und Art. 225 ZEKom-2",
                "bis zum Widerruf",
              ],
              [
                "Versand des Newsletters",
                "Einwilligung — Art. 6 Abs. 1 lit. a DSGVO",
                "bis zur Abmeldung bzw. zum Widerruf",
              ],
              [
                "Geltendmachung oder Abwehr von Rechtsansprüchen",
                "berechtigtes Interesse — Art. 6 Abs. 1 lit. f DSGVO",
                "bis zum Ablauf der Verjährungsfristen",
              ],
            ],
          },
          {
            type: "p",
            text: "Soweit wir uns auf ein berechtigtes Interesse stützen, haben wir eine Abwägung zwischen diesem Interesse und Ihren Rechten vorgenommen. Eine Zusammenfassung stellen wir auf Anfrage unter privacy@nreport.ai bereit.",
          },
        ],
      },
      {
        title: "4. Immobiliendaten und Daten Dritter",
        blocks: [
          {
            type: "p",
            text: "Die Adresse der von Ihnen abgefragten Immobilie ist für uns ein Bestelldatum. Steht die Immobilie mit Ihnen in Verbindung (etwa Ihr Wohnsitz), ist sie zugleich Ihr personenbezogenes Datum und wird wie andere Bestelldaten behandelt.",
          },
          {
            type: "p",
            text: "Ein Bericht kann Daten aus öffentlichen Registern enthalten, die Dritte betreffen — insbesondere wenn für die Immobilie ein Grundbuchauszug mit Eigentums- oder Belastungsangaben verfügbar ist. Wir verarbeiten diese Daten ausschließlich zur Erstellung des von Ihnen bestellten Berichts auf Grundlage des berechtigten Interesses nach Artikel 6 Abs. 1 lit. f DSGVO und nur in dem Umfang, in dem sie im Register bereits öffentlich zugänglich sind.",
          },
          {
            type: "p",
            text: "Wir bilden aus diesen Daten keine Profile von Einzelpersonen, führen keine gesonderte Eigentümerdatenbank und nutzen sie nicht zu Werbezwecken. Da die Daten nicht unmittelbar bei der betroffenen Person erhoben werden und eine Einzelbenachrichtigung mit unverhältnismäßigem Aufwand verbunden wäre, stützen wir uns auf die Ausnahme des Artikels 14 Abs. 5 lit. b DSGVO und veröffentlichen statt einer Einzelinformation diese Erklärung.",
          },
          {
            type: "note",
            text: "Als Besteller sind Sie dafür verantwortlich, ein berechtigtes Interesse an der Abfrage einer konkreten Immobilie zu haben (etwa beabsichtigter Kauf, Miete, Finanzierung, Bewertung oder Eigentum). Der Dienst darf nicht zur Nachverfolgung, Belästigung oder Profilbildung über Einzelpersonen genutzt werden.",
          },
        ],
      },
      {
        title: "5. Quellen, die keine Nutzerdaten sind",
        blocks: [
          {
            type: "p",
            text: "Zur Erstellung der Berichte nutzen wir öffentlich zugängliche Quellen: die Register der Vermessungs- und Kartierungsbehörde der Republik Slowenien (Immobilienkataster, Immobilienregister, Immobilienmarktregister), das Grundbuch, das Register der Energieausweise, kartografische Quellen sowie öffentlich veröffentlichte Immobilieninserate. Diese Quellen unterliegen nicht unserer Kontrolle, sondern den Bedingungen ihrer Betreiber.",
          },
        ],
      },
      {
        title: "6. Cookies und ähnliche Technologien",
        blocks: [
          {
            type: "p",
            text: "Nicht notwendige Cookies und ähnliche Technologien laden wir erst nach Ihrer Einwilligung. Beim ersten Besuch zeigen wir einen Hinweis, in dem Sie alles akzeptieren, ablehnen oder einzelne Kategorien wählen können. Ablehnen ist genauso einfach wie Zustimmen.",
          },
          {
            type: "p",
            text: "Für Anmeldung und Betrieb des Dienstes verwenden wir den lokalen Speicher des Browsers; dies ist zur Erbringung des von Ihnen ausdrücklich gewünschten Dienstes unbedingt erforderlich und daher einwilligungsfrei (Art. 225 Abs. 2 ZEKom-2).",
          },
          { type: "p", text: "Eine detaillierte Aufstellung enthält die Cookie-Richtlinie." },
          { type: "cookieSettings", label: "Cookie-Einstellungen" },
        ],
      },
      {
        title: "7. Empfänger der Daten",
        blocks: [
          {
            type: "p",
            text: "Wir verkaufen keine personenbezogenen Daten. Wir geben sie nur an Auftragsverarbeiter weiter, die nach unseren Weisungen und auf Grundlage eines Auftragsverarbeitungsvertrags (Artikel 28 DSGVO) tätig werden, sowie an Behörden, soweit gesetzlich vorgeschrieben.",
          },
          {
            type: "table",
            head: ["Empfänger", "Zweck", "Verarbeitungsort", "Grundlage der Übermittlung"],
            rows: [
              [
                "Supabase, Inc.",
                "Datenbank, Nutzeranmeldung, Dateispeicher, Serverfunktionen",
                "EU (Frankfurt, eu-central-1); Unternehmen in den USA",
                "Auftragsverarbeitungsvertrag und Standardvertragsklauseln",
              ],
              [
                "Railway Corp.",
                "Hosting des Backend-Servers, Warteschlangen und PDF-Erzeugung",
                "USA / EU",
                "Standardvertragsklauseln",
              ],
              [
                "Vercel Inc.",
                "Hosting und Auslieferung der Website",
                "EU / USA",
                "Standardvertragsklauseln, EU-US Data Privacy Framework",
              ],
              [
                "Cloudflare, Inc.",
                "Netzwerk-Proxy, Schutz vor Missbrauch und Angriffen",
                "globales Netzwerk",
                "Standardvertragsklauseln, EU-US Data Privacy Framework",
              ],
              [
                "Stripe Payments Europe, Ltd.",
                "Zahlungsabwicklung, Betrugsprävention (dafür eigenständig Verantwortlicher)",
                "Irland, USA",
                "Standardvertragsklauseln",
              ],
              [
                "Anthropic, PBC",
                "Erzeugung der Textteile des Berichts mit einem Sprachmodell",
                "USA",
                "Standardvertragsklauseln; keine Nutzung der Daten zum Modelltraining",
              ],
              [
                "Google Ireland Ltd. / Google LLC",
                "Karten, Adresssuche und Geokodierung; Sprachmodell Gemini; Google Analytics (nur bei Einwilligung)",
                "Irland, USA",
                "Standardvertragsklauseln, EU-US Data Privacy Framework",
              ],
              [
                "Meta Platforms Ireland Ltd.",
                "Messung der Werbewirkung (nur bei Marketing-Einwilligung)",
                "Irland, USA",
                "Standardvertragsklauseln, EU-US Data Privacy Framework",
              ],
              [
                "Resend, Inc.",
                "Versand transaktionaler E-Mails",
                "USA",
                "Standardvertragsklauseln",
              ],
              [
                "MailerLite Limited",
                "Newsletter-Versand (nur bei Einwilligung)",
                "Europäische Union",
                "keine Drittlandübermittlung",
              ],
            ],
          },
          {
            type: "p",
            text: "Wir können Daten außerdem an Gerichte, Steuer- oder Aufsichtsbehörden, die Polizei oder eine andere zuständige Stelle offenlegen, soweit wir dazu verpflichtet sind, sowie an unser Steuerbüro, unseren Rechtsanwalt oder Prüfer im erforderlichen Umfang. Bei einer Umstrukturierung oder einem Verkauf des Geschäftsbetriebs können Daten auf den Rechtsnachfolger übergehen; wir informieren Sie vorab.",
          },
        ],
      },
      {
        title: "8. Übermittlungen in Drittländer",
        blocks: [
          {
            type: "p",
            text: "Einige Auftragsverarbeiter haben ihren Sitz oder Support-Einheiten in den Vereinigten Staaten. In diesen Fällen stützt sich die Übermittlung auf den Angemessenheitsbeschluss der Europäischen Kommission zum EU-US Data Privacy Framework (sofern der Empfänger zertifiziert ist) oder auf die Standardvertragsklauseln der Kommission, ergänzt um eine Transfer-Folgenabschätzung und zusätzliche technische Maßnahmen wie Verschlüsselung bei Übertragung und Speicherung sowie Zugriffsbeschränkungen.",
          },
          {
            type: "p",
            text: "Eine Kopie der verwendeten Garantien können Sie unter privacy@nreport.ai anfordern.",
          },
        ],
      },
      {
        title: "9. Automatisierte Entscheidungsfindung und Profiling",
        blocks: [
          {
            type: "p",
            text: "Ein Bericht einschließlich der indikativen Wertschätzung entsteht automatisch ohne menschliche Prüfung. Diese Verarbeitung betrifft die Immobilie und entfaltet gegenüber einer Person keine rechtliche Wirkung und beeinträchtigt sie nicht in ähnlich erheblicher Weise; es handelt sich daher nicht um eine automatisierte Entscheidungsfindung im Sinne des Artikels 22 Abs. 1 DSGVO.",
          },
          {
            type: "p",
            text: "Wir nutzen Ihre personenbezogenen Daten nicht für automatisiertes Kredit- oder Risiko-Scoring oder eine vergleichbare Einstufung von Personen.",
          },
        ],
      },
      {
        title: "10. Sicherheit der Verarbeitung",
        blocks: [
          {
            type: "ul",
            items: [
              "Verschlüsselung bei der Übertragung (TLS) und Verschlüsselung gespeicherter Daten,",
              "Zugriffsbeschränkung nach dem Prinzip der geringsten Rechte und getrennte Administratorzugänge,",
              "Sicherheit auf Zeilenebene in der Datenbank, sodass Nutzer nur ihre eigenen Datensätze erreichen,",
              "Protokollierung administrativer Eingriffe,",
              "regelmäßige Backups und Prüfung ihrer Wiederherstellbarkeit,",
              "Auftragsverarbeitungsverträge und Prüfung der Auftragsverarbeiter.",
            ],
          },
          {
            type: "p",
            text: "Bei einer Verletzung des Schutzes personenbezogener Daten, die voraussichtlich ein hohes Risiko für Ihre Rechte und Freiheiten zur Folge hat, informieren wir Sie unverzüglich und melden die Verletzung innerhalb von 72 Stunden dem Informationsbeauftragten.",
          },
        ],
      },
      {
        title: "11. Ihre Rechte",
        blocks: [
          { type: "p", text: "In Bezug auf Ihre personenbezogenen Daten haben Sie das Recht auf:" },
          {
            type: "ul",
            items: [
              "Auskunft und Erhalt einer Kopie der Daten (Artikel 15 DSGVO),",
              "Berichtigung unrichtiger und Vervollständigung unvollständiger Daten (Artikel 16),",
              "Löschung der Daten, „Recht auf Vergessenwerden“ (Artikel 17),",
              "Einschränkung der Verarbeitung (Artikel 18),",
              "Datenübertragbarkeit in einem maschinenlesbaren Format (Artikel 20),",
              "Widerspruch gegen eine auf berechtigtem Interesse beruhende Verarbeitung sowie jederzeitigen Widerspruch gegen Direktwerbung (Artikel 21),",
              "jederzeitigen Widerruf einer Einwilligung, ohne dass die Rechtmäßigkeit der bis dahin erfolgten Verarbeitung berührt wird (Artikel 7).",
            ],
          },
          {
            type: "p",
            text: "Anfragen richten Sie an privacy@nreport.ai. Wir antworten spätestens innerhalb eines Monats nach Eingang; bei komplexen Anfragen können wir um zwei Monate verlängern und informieren Sie darüber. Die Ausübung der Rechte ist kostenlos; bei offenkundig unbegründeten oder exzessiven Anträgen können wir ein angemessenes Entgelt verlangen oder den Antrag ablehnen. Aus Sicherheitsgründen können wir zusätzliche Angaben zur Identitätsprüfung verlangen.",
          },
          {
            type: "p",
            text: "Die Löschung Ihres Kontos und Ihrer Daten können Sie auch über das dafür vorgesehene Formular auf der Website beantragen. Einen Teil der Daten müssen wir trotz Löschungsantrag aufbewahren, soweit dies gesetzlich vorgeschrieben ist — insbesondere ausgestellte Rechnungen für die steuerliche Aufbewahrungsfrist.",
          },
          {
            type: "p",
            text: "Wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer Daten gegen geltendes Recht verstößt, haben Sie das Recht auf Beschwerde bei der Aufsichtsbehörde:",
          },
          {
            type: "links",
            items: [
              {
                label:
                  "Informationsbeauftragter der Republik Slowenien, Dunajska cesta 22, 1000 Ljubljana — www.ip-rs.si",
                href: "https://www.ip-rs.si/",
              },
            ],
          },
          {
            type: "p",
            text: "Sie können sich auch an die Aufsichtsbehörde Ihres gewöhnlichen Aufenthalts oder des Orts des mutmaßlichen Verstoßes wenden.",
          },
        ],
      },
      {
        title: "12. Ist die Bereitstellung der Daten verpflichtend",
        blocks: [
          {
            type: "p",
            text: "Die Angabe Ihrer E-Mail-Adresse und der Immobiliendaten ist Voraussetzung für Abschluss und Erfüllung des Vertrags — ohne sie können wir keinen Bericht erstellen und liefern. Die Angabe von Daten für den Newsletter und die Einwilligung in nicht notwendige Cookies sind vollständig freiwillig; ihre Verweigerung wirkt sich nicht auf die Nutzung des Dienstes aus.",
          },
        ],
      },
      {
        title: "13. Kinder",
        blocks: [
          {
            type: "p",
            text: "Der Dienst richtet sich nicht an Personen unter 18 Jahren; deren Daten erheben wir nicht wissentlich. Stellen wir fest, dass wir Daten Minderjähriger ohne geeignete Grundlage verarbeitet haben, löschen wir sie. Wenn Sie vermuten, dass dies geschehen ist, informieren Sie uns unter privacy@nreport.ai.",
          },
        ],
      },
      {
        title: "14. Links zu anderen Websites",
        blocks: [
          {
            type: "p",
            text: "Die Website enthält Links zu Seiten Dritter (etwa amtlichen Registern oder Inserateportalen). Für deren Inhalte und deren Umgang mit personenbezogenen Daten übernehmen wir keine Verantwortung; wir empfehlen, deren Datenschutzerklärungen zu lesen.",
          },
        ],
      },
      {
        title: "15. Änderungen dieser Erklärung",
        blocks: [
          {
            type: "p",
            text: "Wir können diese Erklärung anpassen, wenn sich der Dienst, unsere Auftragsverarbeiter oder die Rechtslage ändern. Jede Fassung trägt das Datum der letzten Aktualisierung. Über wesentliche Änderungen informieren wir registrierte Nutzer per E-Mail oder durch einen Hinweis auf der Website; erfordert eine Änderung Ihre Einwilligung, holen wir diese gesondert ein.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  cookies: {
    eyebrow: legalEyebrow,
    title: "Cookie-Richtlinie",
    updated,
    intro:
      "Diese Richtlinie erläutert, welche Cookies und ähnlichen Technologien die von der Noventis d.o.o. betriebene Website nreport.ai verwendet, warum sie das tut und wie Sie Ihre Auswahl jederzeit ändern können.",
    sections: [
      {
        title: "1. Was Cookies sind",
        blocks: [
          {
            type: "p",
            text: "Cookies sind kleine Textdateien, die eine Website in Ihrem Browser speichert. Eine ähnliche Rolle spielen lokaler Speicher, Sitzungsspeicher und Zählpixel. In dieser Richtlinie werden sie zusammenfassend als „Cookies“ bezeichnet.",
          },
          {
            type: "p",
            text: "Cookies ermöglichen es einer Website, Ihre Anmeldung und Einstellungen zu speichern; einige dienen außerdem der Reichweitenmessung und der Werbung.",
          },
        ],
      },
      {
        title: "2. Rechtsgrundlage und Ihre Einwilligung",
        blocks: [
          {
            type: "p",
            text: "Nach Artikel 225 Abs. 1 des slowenischen Gesetzes über elektronische Kommunikation (ZEKom-2) und Artikel 6 Abs. 1 lit. a DSGVO speichern und lesen wir nicht notwendige Cookies erst nach Ihrer Einwilligung. Die Einwilligung ist freiwillig, bestimmt und unmissverständlich; die Ablehnung ist genauso einfach wie die Zustimmung.",
          },
          {
            type: "p",
            text: "Unbedingt erforderliche Cookies sind einwilligungsfrei, weil sie zur Erbringung des von Ihnen ausdrücklich gewünschten Dienstes notwendig sind (Artikel 225 Abs. 2 ZEKom-2).",
          },
          {
            type: "p",
            text: "Sie können Ihre Einwilligung jederzeit widerrufen oder ändern. Der Widerruf berührt nicht die Rechtmäßigkeit der bis dahin erfolgten Verarbeitung.",
          },
          { type: "cookieSettings", label: "Cookie-Einstellungen" },
        ],
      },
      {
        title: "3. Von uns verwendete Cookie-Kategorien",
        subsections: [
          {
            title: "Unbedingt erforderlich (immer aktiv)",
            blocks: [
              {
                type: "p",
                text: "Sie ermöglichen Anmeldung, Sitzungserhalt, Formularsicherheit und Zahlung. Ohne sie funktioniert die Website nicht. Sie sind einwilligungsfrei und lassen sich nicht abschalten.",
              },
              {
                type: "table",
                head: ["Name / Technologie", "Anbieter", "Zweck", "Dauer"],
                rows: [
                  [
                    "sb-…-auth-token (lokaler Speicher)",
                    "nReport / Supabase",
                    "Aufrechterhaltung der Anmeldung",
                    "bis zur Abmeldung oder zum Löschen der Browserdaten",
                  ],
                  [
                    "nreport-cookie-consent (lokaler Speicher)",
                    "nReport",
                    "Speicherung Ihrer Cookie-Auswahl",
                    "bis zum Widerruf oder Löschen der Browserdaten",
                  ],
                  [
                    "selectedAddress, selectedPropertyType u. Ä. (lokaler Speicher)",
                    "nReport",
                    "Erhalt eingegebener Angaben zwischen den Bestellschritten",
                    "bis zum Abschluss der Bestellung",
                  ],
                  [
                    "metaPurchaseContext (Sitzungsspeicher)",
                    "nReport",
                    "Übergabe der Kaufangaben an die Bestätigungsseite",
                    "bis zum Schließen des Tabs",
                  ],
                  [
                    "__stripe_mid, __stripe_sid",
                    "Stripe",
                    "Zahlungsabwicklung und Betrugsprävention; nur auf der Zahlungsseite gesetzt",
                    "1 Jahr / 30 Minuten",
                  ],
                ],
              },
            ],
          },
          {
            title: "Analyse (nur mit Ihrer Einwilligung)",
            blocks: [
              {
                type: "p",
                text: "Sie helfen uns zu verstehen, wie viele Besucher die Website hat, welche Seiten sie aufrufen und wo Probleme auftreten. Die Daten nutzen wir zur Verbesserung des Dienstes.",
              },
              {
                type: "table",
                head: ["Name", "Anbieter", "Zweck", "Dauer"],
                rows: [
                  [
                    "_ga, _ga_*",
                    "Google Analytics 4 (Google Ireland Ltd.)",
                    "Unterscheidung von Besuchern und Reichweitenmessung",
                    "bis zu 2 Jahre",
                  ],
                  [
                    "_gid, _gat",
                    "Google Analytics 4",
                    "Unterscheidung von Besuchern, Drosselung der Anfragerate",
                    "24 Stunden / 1 Minute",
                  ],
                ],
              },
              {
                type: "p",
                text: "Google Analytics setzen wir mit Consent Mode ein; ohne Ihre Einwilligung wird das Skript gar nicht geladen, Google Signals und Werbepersonalisierung sind deaktiviert.",
              },
            ],
          },
          {
            title: "Marketing (nur mit Ihrer Einwilligung)",
            blocks: [
              {
                type: "p",
                text: "Sie ermöglichen die Messung des Erfolgs unserer Werbekampagnen und die Ausspielung relevanterer Anzeigen auf Meta-Plattformen.",
              },
              {
                type: "table",
                head: ["Name", "Anbieter", "Zweck", "Dauer"],
                rows: [
                  [
                    "_fbp",
                    "Meta Platforms Ireland Ltd.",
                    "Wiedererkennung des Browsers zur Conversion-Messung",
                    "bis zu 3 Monate",
                  ],
                  [
                    "_fbc",
                    "Meta Platforms Ireland Ltd.",
                    "Erfassung des Anzeigenklicks, über den Sie auf die Seite kamen",
                    "bis zu 3 Monate",
                  ],
                ],
              },
              {
                type: "p",
                text: "Erlauben Sie keine Marketing-Cookies, laden wir das Meta-Pixel nicht und melden Ihren Kauf auch nicht serverseitig an Meta.",
              },
            ],
          },
        ],
      },
      {
        title: "4. Einwilligung ändern oder widerrufen",
        blocks: [
          {
            type: "p",
            text: "Sie können Ihre Auswahl jederzeit über die Schaltfläche unten oder über den Link „Cookie-Einstellungen“ in der Fußzeile der Website ändern.",
          },
          { type: "cookieSettings", label: "Cookie-Einstellungen" },
          {
            type: "p",
            text: "Beim Widerruf laden wir die betreffenden Skripte nicht mehr und löschen die Cookies, die wir entfernen können. Bereits in Ihrem Browser gespeicherte Cookies können Sie auch selbst über die Browsereinstellungen entfernen.",
          },
        ],
      },
      {
        title: "5. Cookies im Browser verwalten",
        blocks: [
          {
            type: "ul",
            items: [
              "Chrome: Einstellungen › Datenschutz und Sicherheit › Cookies von Drittanbietern",
              "Firefox: Einstellungen › Datenschutz & Sicherheit › Cookies und Website-Daten",
              "Safari: Einstellungen › Datenschutz › Website-Daten verwalten",
              "Edge: Einstellungen › Cookies und Websiteberechtigungen",
            ],
          },
          {
            type: "p",
            text: "Das Blockieren aller Cookies kann Anmeldung und Kauf verhindern, weil die Website Ihre Sitzung nicht aufrechterhalten kann.",
          },
        ],
      },
      {
        title: "6. Richtlinien externer Anbieter",
        blocks: [
          {
            type: "links",
            items: [
              { label: "Google — Datenschutzerklärung", href: "https://policies.google.com/privacy" },
              { label: "Meta — Datenschutzrichtlinie", href: "https://www.facebook.com/privacy/policy" },
              { label: "Stripe — Datenschutzerklärung", href: "https://stripe.com/privacy" },
              { label: "Supabase — Datenschutzerklärung", href: "https://supabase.com/privacy" },
            ],
          },
        ],
      },
      {
        title: "7. Änderungen und Kontakt",
        blocks: [
          {
            type: "p",
            text: "Wir aktualisieren diese Cookie-Richtlinie, wenn sich die eingesetzten Technologien ändern. Ändert sich der Cookie-Bestand wesentlich, holen wir Ihre Einwilligung erneut ein.",
          },
          {
            type: "p",
            text: "Bei Fragen zu Cookies schreiben Sie an privacy@nreport.ai. Mehr zur Verarbeitung personenbezogener Daten finden Sie in der Datenschutzerklärung.",
          },
        ],
      },
    ],
  } satisfies LegalDocument,

  dataDeletion: {
    title: "Löschung personenbezogener Daten",
    subtitle:
      "Beantragen Sie die Löschung Ihrer personenbezogenen Daten nach Artikel 17 der Datenschutz-Grundverordnung (DSGVO).",
    alert:
      "Wichtig: Die Löschung ist unwiderruflich. Danach haben Sie keinen Zugriff mehr auf Ihr Konto, gekaufte Credits und erstellte Berichte.",
    formTitle: "Antrag auf Datenlöschung",
    formDesc: "Füllen Sie das folgende Formular aus, um die Löschung Ihrer Daten zu beantragen.",
    emailLabel: "E-Mail-Adresse *",
    emailPlaceholder: "ihre.email@beispiel.de",
    emailHint: "Geben Sie die mit Ihrem Konto verknüpfte E-Mail-Adresse ein.",
    reasonLabel: "Grund für die Löschung (optional)",
    reasonPlaceholder: "Beschreiben Sie den Grund für die Löschung...",
    deletedDataTitle: "Diese Daten werden gelöscht:",
    deletedDataItems: [
      "Kontodaten (E-Mail, Name, Einstellungen)",
      "Bestellhistorie und Guthabenverbrauch",
      "Gespeicherte Berichte und Analysen",
      "Hochgeladene Dateien und Anhänge",
      "Einwilligungsnachweise und Präferenzen",
    ],
    submit: "Löschantrag senden",
    contact: "Fragen? Kontaktieren Sie uns unter",
    successTitle: "Antrag erfolgreich gesendet",
    successDesc:
      "Wir haben Ihren Antrag erhalten und bearbeiten ihn spätestens innerhalb eines Monats nach Eingang, wie Artikel 12 DSGVO es vorsieht.",
    processingTitle: "Bearbeitungszeit",
    processingDesc:
      "Wir bearbeiten Anträge spätestens innerhalb eines Monats. Ausgestellte Rechnungen müssen wir aus steuerrechtlichen Gründen 10 Jahre aufbewahren; sie bleiben daher auch nach der Kontolöschung gespeichert.",
    securityTitle: "Datensicherheit",
    securityDesc:
      "Vor der Löschung können wir eine Bestätigung Ihrer Identität verlangen. Die Daten werden sicher aus unseren Systemen und spätestens innerhalb von 30 Tagen aus den Backups gelöscht.",
    backHome: "Zurück zur Startseite",
    toastEmailRequired: "Bitte geben Sie Ihre E-Mail-Adresse ein.",
    toastSendFailed: "Ihr Antrag konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie an info@nreport.ai.",
    toastSuccessTitle: "Antrag gesendet",
    toastSuccessDesc: "Ihr Antrag auf Datenlöschung wurde erfolgreich gesendet.",
  },
};
